import type { ButtonHTMLAttributes, ReactNode } from 'react';

import { cn } from '@/shared/lib/cn';

interface ChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
  /** Цвет-акцент (обычно цвет сферы) как CSS-значение. */
  accent?: string;
  icon?: ReactNode;
  size?: 'sm' | 'md';
}

/** Фильтр или переключаемая метка. Используется для выбора сферы, статуса и т.п. */
export function Chip({
  selected = false,
  accent,
  icon,
  size = 'md',
  className,
  children,
  type = 'button',
  style,
  ...rest
}: ChipProps) {
  return (
    <button
      type={type}
      aria-pressed={selected}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border font-medium',
        'transition-[background-color,border-color,transform] duration-150 active:scale-95',
        // Чип остаётся визуально компактным, но зона касания добирается до
        // нормы: фильтры стоят в ряд, и промах по соседнему — обычное дело.
        'tap-target',
        size === 'sm' ? 'h-7 px-2.5 text-[12px]' : 'h-9 px-3.5 text-[13px]',
        // Фон выбранного чипа задаётся здесь, а не только инлайновым акцентом.
        //
        // Раньше выбранное состояние давало белый текст, а фон появлялся лишь
        // если вызывающий код передал `accent`. Забыл передать — получался
        // белый текст на прозрачном фоне: чип занимал место в ряду и нажимался,
        // но его не было видно. Так пропадали «Все» в «Книгах и фильмах» и в
        // привычках — причём именно этот фильтр выбран по умолчанию.
        //
        // Инлайновый `accent` перекрывает этот фон, поэтому цветные чипы сфер
        // остаются цветными: у инлайнового стиля приоритет выше класса.
        selected
          ? 'border-transparent bg-brand text-brand-contrast'
          : 'border-border bg-surface-2 text-muted hover:text-content',
        className,
      )}
      style={{
        ...(selected && accent ? { backgroundColor: accent } : {}),
        ...style,
      }}
      {...rest}
    >
      {icon}
      {children}
    </button>
  );
}

interface BadgeProps {
  children: ReactNode;
  tone?: 'neutral' | 'brand' | 'success' | 'warning' | 'danger' | 'info';
  accent?: string;
  className?: string;
}

const TONES = {
  neutral: 'bg-surface-3 text-muted',
  brand: 'bg-brand-soft text-brand-soft-text',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
  info: 'bg-info-soft text-info',
} as const;

/** Некликабельная метка статуса. */
export function Badge({ children, tone = 'neutral', accent, className }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full px-2 py-0.5',
        'text-[12px] font-medium whitespace-nowrap',
        !accent && TONES[tone],
        className,
      )}
      style={
        accent
          ? { backgroundColor: `color-mix(in oklch, ${accent} 18%, transparent)`, color: accent }
          : undefined
      }
    >
      {children}
    </span>
  );
}
